import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Crown, Search, Trash2, UserPlus } from "lucide-react";
import { Stagger, StaggerItem } from "@/components/FadeIn";
import { M3BottomSheet } from "@/components/M3BottomSheet";
import { MemberAvatar } from "@/components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { useSupabaseQuery } from "@/lib/dbHooks";
import { supabase, isAuthConfigured } from "@/lib/supabase";
import { InitialsPill } from "@/pages/modules/admin/InitialsPill";
import type { Database } from "@/types/db";

// RpcSignature: a structural type matching any Supabase RPC that
// returns a single jsonb row. The cast `supabase.rpc as unknown as
// RpcSignature` is required because the generated DB types only
// know about whitelisted function names; our new
// add_family_member_safe (migration 04958) isn't in that whitelist.
type RpcSignature = (
  name: string,
  args: Record<string, unknown>
) => Promise<{ data: unknown; error: { message: string } | null }>;


type MemberRow = Database["public"]["Tables"]["family_members"]["Row"];

/* Sheet state for assigning an existing user to the family. The
 * flow is: type an email → look up via lookup_user_for_family_add
 * RPC → if a candidate is found and is family-less, choose a role
 * → submit. assign_existing_user_to_family does the actual
 * family_members INSERT + chat_thread_members fan-out.
 *
 * `phase` tracks the multi-step UX:
 *   • "search"   — collecting email input.
 *   • "found"    — candidate loaded, role picker is interactive.
 *   • "searching" — RPC in flight (loading spinner on Search button).
 *   • "submitting" — assign RPC in flight.
 *
 * The `notice` string doubles as the search-error surface
 * ("冇搵到呢個電郵") and the post-assign success message; we
 * clear it on every state transition so the user doesn't see a
 * stale error after a successful submit. */
type AddMemberSheet = {
  open: boolean;
  phase: "search" | "searching" | "found" | "submitting";
  email: string;
  candidate: CandidateRow | null;
  role: "admin" | "member";
  notice: string | null;
};

type CandidateRow = {
  user_id: string;
  display_name: string;
  initials: string;
  avatar_color: string;
  profile_role: string;
  current_family_id: string | null;
};

const EMPTY_ADD: AddMemberSheet = {
  open: false,
  phase: "search",
  email: "",
  candidate: null,
  role: "member",
  notice: null,
};

/**
 * LITE cap: PRD §7 limits each family to 6 members including the
 * family admin. Enforced client-side (disable the Add button + show
 * a friendly caption) and server-side by
 * assign_existing_user_to_family (migration 04974). Keep this
 * constant in one place — `MAX_MEMBERS` — and the build will
 * typecheck any drift between the two enforcement points.
 */
const MAX_MEMBERS = 6;

// Server's error string for the cap case. Match the message the
// assign_existing_user_to_family RPC raises so we can recognise
// the LITE cap in the notice and re-use the same UI copy.
const SERVER_CAP_MESSAGE_FRAGMENT = "LITE cap";

// User-facing message for the LITE cap. Used by the local guard in
// submitAdd and as the notice when the server-side RPC raises the
// cap error. Keep these in lockstep with the
// SERVER_CAP_MESSAGE_FRAGMENT check above.
const CAP_NOTICE_REMOVE_FIRST = `已達 ${MAX_MEMBERS} 位成員上限，請先移除一位成員。`;

export function ModuleMembersView() {
  const me0 = useCurrentUser();
  const cf  = useCurrentFamily(me0.user?.id ?? null);
  const [tick, setTick] = useState(0);
  const [add, setAdd] = useState<AddMemberSheet>(EMPTY_ADD);

  // Family-admin status comes from profiles.role (migration 024),
  // not from family_members.role. A user is either a family_admin
  // (LITE admin) or a family_member in the LITE family app.
  const isFamilyAdmin = me0.role === "family_admin";

  // Look up profiles.role for each family member so the chip can
  // show "管理員" for the family admin. Joined via user_id; family
  // members without a user_id (placeholder rows) just have no role.
  const memberProfileRole = useSupabaseQuery<Record<string, string>>(async () => {
    if (!cf.family?.id) return { data: {}, error: null };
    const ids = (cf.members ?? [])
      .map((m) => m.user_id)
      .filter((x): x is string => !!x);
    if (ids.length === 0) return { data: {}, error: null };
    const { data, error } = await supabase
      .from("profiles")
      .select("id, role")
      .in("id", ids);
    const map: Record<string, string> = {};
    (data ?? []).forEach((p) => { map[p.id] = p.role; });
    return { data: map, error };
  }, [cf.family?.id, tick]);
  const isMemberFamilyAdmin = (m: MemberRow): boolean =>
    m.user_id != null && memberProfileRole.data?.[m.user_id] === "family_admin";

  // Local refresh trigger for the members list. useSupabaseQuery
  // re-fetches when its deps change. We piggyback on cf.members +
  // tick so any change forces a re-fetch.
  const membersDepKey = useMemo(
    () => `${tick}|${cf.members.length}`,
    [tick, cf.members.length],
  );
  const members = useSupabaseQuery<MemberRow[]>(async () => {
    if (!cf.family?.id) return { data: [], error: null };
    return await supabase
      .from("family_members")
      .select("*")
      .eq("family_id", cf.family.id)
      .order("created_at", { ascending: true });
  }, [cf.family?.id, membersDepKey]);

  const refresh = () => setTick((t) => t + 1);

  // Two-step flow: lookup_user_for_family_add first (returns the
  // candidate row), then assign_existing_user_to_family on the
  // success branch. The step split is intentional — the lookup
  // tells the user the candidate is in fact registerable (not
  // already a member of another family, not a system role, etc.)
  // BEFORE they pick a role and tap 加入, instead of failing
  // half-way through the assign RPC.
  const runSearch = async () => {
    if (!isAuthConfigured()) {
      setAdd((a) => ({ ...a, notice: "Supabase 環境未配置。請檢查 .env。" }));
      return;
    }
    if (!add.email.includes("@")) {
      setAdd((a) => ({ ...a, notice: "請輸入有效的電郵地址。" }));
      return;
    }
    setAdd((a) => ({ ...a, phase: "searching", notice: null, candidate: null }));
    const { data, error } = await (supabase.rpc as unknown as RpcSignature)(
      "lookup_user_for_family_add",
      {
        p_email:     add.email.trim().toLowerCase(),
        p_family_id: cf.family?.id,
      },
    );
    if (error) {
      setAdd((a) => ({
        ...a,
        phase: "search",
        notice: rlsMessage(error),
      }));
      return;
    }
    const rows = Array.isArray(data) ? data : data ? [data] : [];
    const first = rows[0] as CandidateRow | undefined;
    if (!first) {
      setAdd((a) => ({
        ...a,
        phase: "search",
        notice: "搵唔到呢個電郵嘅用戶。請確認對方已經註冊 FamChest 帳戶。",
      }));
      return;
    }
    setAdd((a) => ({ ...a, phase: "found", candidate: first, notice: null }));
  };

  const submitAdd = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isAuthConfigured()) {
      setAdd((a) => ({ ...a, notice: "Supabase 環境未配置。請檢查 .env。" }));
      return;
    }
    if (atCap) {
      setAdd((a) => ({ ...a, notice: CAP_NOTICE_REMOVE_FIRST }));
      return;
    }
    if (!add.candidate) {
      setAdd((a) => ({ ...a, notice: "請先搜尋成員嘅電郵。" }));
      return;
    }
    if (add.candidate.current_family_id) {
      setAdd((a) => ({
        ...a,
        notice: "呢位用戶已經屬於另一個家庭。請對方先退出原家庭再加入。",
      }));
      return;
    }
    setAdd((a) => ({ ...a, phase: "submitting", notice: null }));
    const { data, error } = await (supabase.rpc as unknown as RpcSignature)(
      "assign_existing_user_to_family",
      {
        p_user_id:   add.candidate.user_id,
        p_family_id: cf.family?.id,
        p_role:      add.role,
      },
    );
    if (error || !data) {
      const raw = error?.message ?? "未收到回應";
      const isCap = raw.includes(SERVER_CAP_MESSAGE_FRAGMENT);
      setAdd((a) => ({
        ...a,
        phase: "found",
        notice: isCap ? CAP_NOTICE_REMOVE_FIRST : rlsMessage(error ?? { message: raw }),
      }));
      return;
    }
    // Success. Show the message, refresh both local + family-scope
    // queries, and let the auto-close effect reset the sheet.
    setAdd((a) => ({
      ...a,
      phase: "submitting", // effect keys off this + notice
      notice: `已將「${add.candidate?.display_name ?? add.email}」加入家庭。`,
    }));
    refresh();
    cf.refresh();
  };

  // Reset the sheet to a clean state when the user closes it. Done
  // via an effect so re-opening after a search-then-close restores
  // the email field — same behaviour the previous password form
  // had (the old `useEffect` keyed off `add.submitting && add.open`).
  useEffect(() => {
    if (!add.open) setAdd(EMPTY_ADD);
  }, [add.open]);

  // Auto-close the sheet 1.2s after a successful add. The success
  // notice stays visible long enough for the user to read it.
  useEffect(() => {
    if (add.phase === "submitting" && add.notice?.startsWith("已將")) {
      const t = window.setTimeout(() => setAdd(EMPTY_ADD), 1200);
      return () => window.clearTimeout(t);
    }
  }, [add.phase, add.notice]);
  useEffect(() => {
    if (!add.open) setAdd(EMPTY_ADD);
  }, [add.open]);

  const removeMember = async (member: MemberRow) => {
    if (member.user_id === me0.user?.id) {
      window.alert("你不能從這裏刪除自己。如要退出家庭，請聯絡其他管理員。");
      return;
    }
    const yes = window.confirm(
      `確定要從家庭中移除「${member.display_name}」？\n` +
      "（該用戶的帳號不會被刪除，只是離開你的家庭。）",
    );
    if (!yes) return;
    const { error } = await supabase
      .from("family_members")
      .delete()
      .eq("id", member.id);
    if (error) {
      window.alert(`移除失敗：${error.message}`);
      return;
    }
    refresh();
    cf.refresh();
  };

  // ====== Render ======

  const list = members.data ?? cf.members;
  // LITE cap. `atCap` is computed off the same list the UI renders so
  // the Add button, the in-sheet form, and the server-side 422 all
  // agree on the same number. Server is the source of truth — this is
  // just a UI guard for the common case.
  const atCap = list.length >= MAX_MEMBERS;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-2">
        <h1 className="font-serif text-2xl font-semibold tracking-tight">
          家庭成員
        </h1>
        <p className="text-sm text-muted-foreground">
          {cf.family ? (
            <>「{cf.family.name}」共 {list.length} / {MAX_MEMBERS} 位成員。</>
          ) : (
            <>載入中…</>
          )}
        </p>
        {isFamilyAdmin ? (
          <p className="text-xs text-muted-foreground">
            你是這個家庭的管理員，可以新增成員、設定角色、移除成員。
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            你是一般成員。只有管理員可以新增、移除成員或調整角色。
          </p>
        )}
      </header>

      {isFamilyAdmin && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {atCap && (
            <span
              role="status"
              className="rounded-full border border-amber-300/70 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800"
            >
              已達 {MAX_MEMBERS} 位成員上限。升級 Core 可擴充。
            </span>
          )}
          <Button
            onClick={() => setAdd({ ...EMPTY_ADD, open: true })}
            disabled={atCap}
            className="gap-2"
            title={atCap ? `已達 ${MAX_MEMBERS} 位成員上限` : undefined}
          >
            <UserPlus className="h-4 w-4" aria-hidden />
            新增成員
          </Button>
        </div>
      )}

      <Stagger className="space-y-3">
        <AnimatePresence>
          {list.map((m) => (
            <StaggerItem key={m.id}>
              <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <MemberAvatar
                    initials={m.initials || m.display_name.slice(0, 1)}
                    color={m.avatar_color || "from-primary to-primary/70"}
                    avatarUrl={m.id === cf.self?.id ? (me0.avatarUrl ?? null) : null}
                    size="lg"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {m.display_name}
                      </span>
                      {isMemberFamilyAdmin(m) ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                          <Crown className="h-3 w-3" aria-hidden />
                          管理員
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
                          成員
                        </span>
                      )}
                      {m.id === cf.self?.id && (
                        <span className="text-[10px] text-muted-foreground">
                          (你)
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {m.user_id ? `已連結帳號` : "未連結（佔位成員）"}
                    </div>
                  </div>
                  {isFamilyAdmin && m.id !== cf.self?.id && !isMemberFamilyAdmin(m) && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(m)}
                        className="h-8 px-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        aria-label={`移除 ${m.display_name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </StaggerItem>
          ))}
        </AnimatePresence>
        {list.length === 0 && (
          <div className="rounded-2xl border border-border/60 bg-card p-6 text-center text-sm text-muted-foreground">
            目前沒有成員。{isFamilyAdmin && "請按「新增成員」開始。"}
          </div>
        )}
      </Stagger>

      {/* ====== Add-member sheet (assign existing user) ====== */}
      {add.open && (
        <M3BottomSheet
          onClose={() => add.phase === "submitting" ? null : setAdd(EMPTY_ADD)}
          title="新增成員"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // The form submit fires on Enter from the email
              // input. Map it to the right RPC depending on
              // phase: searching is the lookup, found is the
              // assign, submitting is a no-op (button is
              // disabled).
              if (add.phase === "search" || add.phase === "searching") {
                void runSearch();
              } else if (add.phase === "found") {
                void submitAdd(e);
              }
            }}
            noValidate
            className="space-y-4"
          >
            <p className="text-xs text-muted-foreground">
              輸入對方嘅 FamChest 電郵，我哋會喺用戶資料庫搵到
              佢，然後直接加入你嘅家庭。對方唔使重新註冊，佢哋
              嘅舊帳戶會保留。
            </p>

            {/* Step 1 — email input + search button. JS-only
                validation (inputMode="email" for mobile
                keyboards, `email.includes('@')` for the truth
                check). noValidate disables HTML5 popups. */}
            <div className="space-y-2">
              <Label htmlFor="add-email">對方嘅 FamChest 電郵</Label>
              <div className="flex gap-2">
                <Input
                  id="add-email"
                  type="text"
                  inputMode="email"
                  autoComplete="off"
                  placeholder="member@family.hk（只須含 @ 即可）"
                  value={add.email}
                  onChange={(e) =>
                    setAdd((a) => ({
                      ...a,
                      email: e.currentTarget.value,
                      // Edit the email after a previous search →
                      // reset the candidate so the user can't
                      // submit a stale profile for the new
                      // address.
                      candidate: a.candidate,
                      phase: a.phase === "found" ? "search" : a.phase,
                    }))
                  }
                  disabled={add.phase === "searching" || add.phase === "submitting"}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={() => void runSearch()}
                  disabled={
                    add.phase === "searching" ||
                    add.phase === "submitting" ||
                    !add.email.includes("@")
                  }
                  className="shrink-0"
                >
                  <Search className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {add.phase === "searching" ? "搵緊…" : "搜尋"}
                </Button>
              </div>
            </div>

            {/* Step 2 — candidate card + role picker. Renders
                only after a successful search. The card shows
                enough info for the admin to confirm they're
                adding the right person, and exposes the role
                picker below. */}
            {add.candidate && add.phase !== "searching" ? (
              <div className="space-y-3 rounded-2xl border border-border/60 bg-card p-3">
                <div className="flex items-center gap-3">
                  <InitialsPill
                    initials={add.candidate.initials}
                    color={add.candidate.avatar_color}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">
                      {add.candidate.display_name}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {add.candidate.profile_role === "family_admin"
                        ? "現有家庭管理員"
                        : add.candidate.profile_role === "family_member"
                          ? "現有家庭成員"
                          : "用戶"}
                      {add.candidate.current_family_id
                        ? " · 已屬於其他家庭"
                        : " · 尚未加入家庭"}
                    </div>
                  </div>
                </div>

                {add.candidate.current_family_id ? (
                  <p className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                    呢位用戶已經屬於另一個家庭。請對方先退出原家庭再加入。
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>角色</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={add.role === "member" ? "default" : "outline"}
                          onClick={() =>
                            setAdd((a) => ({ ...a, role: "member" }))
                          }
                          className="flex-1"
                        >
                          成員
                        </Button>
                        <Button
                          type="button"
                          variant={add.role === "admin" ? "default" : "outline"}
                          onClick={() =>
                            setAdd((a) => ({ ...a, role: "admin" }))
                          }
                          className="flex-1"
                        >
                          <Crown className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                          管理員
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        預設角色是「成員」。提升為管理員後，該成員
                        也可以管理家庭成員。
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {add.notice && (
              <div
                role="status"
                className={cn(
                  "rounded-md border px-3 py-2 text-xs",
                  add.notice.startsWith("已將")
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-rose-300 bg-rose-50 text-rose-700",
                )}
              >
                {add.notice}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setAdd(EMPTY_ADD)}
                disabled={add.phase === "submitting"}
              >
                取消
              </Button>
              {add.phase === "found" && add.candidate && !add.candidate.current_family_id ? (
                <Button type="submit">
                  <UserPlus className="mr-1.5 h-4 w-4" aria-hidden />
                  加入家庭
                </Button>
              ) : null}
            </div>
          </form>
        </M3BottomSheet>
      )}
    </div>
  );
}

/* Translate a Supabase / RPC error into a user-facing string.
 * The default `error.message` is opaque ("function ... does not
 * exist", "invalid input syntax for type uuid", "duplicate key
 * value violates unique constraint"...) — none of which the
 * family admin should see. Map the common Postgres SQLSTATEs
 * to plain Chinese. Anything we don't recognise falls back
 * to the raw message so the user can still copy-paste it. */
function rlsMessage(err: { message?: string; code?: string } | null): string {
  const m = (err?.message ?? "").toLowerCase();
  const c = err?.code;
  if (c === "42501" || m.includes("row-level security") || m.includes("not a family_admin")) {
    return "你唔係呢個家庭嘅管理員，冇權限加入成員。";
  }
  if (c === "23514" && m.includes("lite cap")) {
    return "已達 LITE 6 位成員上限，請先移除一位成員。";
  }
  if (c === "23514" && m.includes("already a member")) {
    return "呢位用戶已經屬於另一個家庭。請對方先退出原家庭再加入。";
  }
  if (c === "23505") {
    return "已經有同名成員，請聯絡管理員。";
  }
  if (c === "22023") {
    return "輸入嘅資料唔啱格式。";
  }
  if (m.includes("invalid input syntax for type uuid")) {
    return "電郵地址或家庭編號無效，請重新整理再試。";
  }
  return err?.message ?? "加入失敗，請重試。";
}
